/* eslint-disable max-len */
import { Arr, VoidCallback1 } from '@deqo/foundation';
import { darken, tint } from 'polished';
import React, { useMemo, useState } from 'react';
import styled from 'styled-components';
import { Obj } from '../../CodesForFoundation';
import { ApiClient } from '../../api/ApiClient';
import { v2 } from '../../api/ApiInterfaceV2';
import { useSubmitter } from '../../platform/Hook';
import { HandwritingFontList } from '../../platform/assets/InAppFontList';
import { ResultInfoForSubmitter } from '../../ui/ResultInfo';
import { useResolve } from '../Container';
import { ProfileModel } from '../ProfileModel';
import { TextAlignment } from '../canvas/entities/TextConventions';
import { TextRuler } from '../canvas/visualization/TextRuler';
import { Counter } from '../common/Users';
import { Copy, Flexer, Spacer } from '../dashboard/elements/LayoutElements';
import { ContrastColorList } from '../packages/tabularImport/strategies/ContrastColorList';
import { Colors, Select } from './../../ui';

const FakeSticky = styled.div`
    position: relative;
    display: flex;
    width: 150px;
    height: 150px;
    color: rgb(51, 51, 51);
    font-family: Mali;
    font-size: 20.5px;
    font-weight: 400;
    line-height: 25.625px;
    padding: 10px;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    box-sizing: border-box;
    overflow: hidden;
    text-align: left;
    justify-content: flex-start;
    align-items: flex-start;
    user-select: none;

    border-radius: .125rem;
    box-shadow: 0px 3px 10px 0 rgba(0, 0, 0, 0.1);
    transform: rotate(1deg);

    &:hover {
        transform: rotate(0deg);
    }

    transition: transform 100ms linear;
`;

const HandwritingView = React.memo<{ handwriting: string, onChange: VoidCallback1<string> }>(({ handwriting, onChange }) => {

    const selected = HandwritingFontList.find(x => x.label === handwriting) ?? HandwritingFontList[0];

    const color = useMemo(() => Arr.random(ContrastColorList), []);
    const text = 'Almost before we knew it, we had left the ground.';
    const settings = {
        fontFamily: selected.label,
        fontColor: '#333',
        fontSize: 0,
        fontStyle: 'normal',
        fontWeight: 400,
        lineHeight: 1.25,
        maxFontSize: 24,
        textMargin: 10,
        textAlignH: TextAlignment.start,
        textAlignV: TextAlignment.start,
    };
    const size = TextRuler.fit(text, settings, 150, 150);
    const styling = {
        fontSize: size,
        fontFamily: selected.label,
        background: tint(.66, color),
    };

    return <>
        <Copy>Your Handwriting:</Copy>
        <Flexer flex="row" width="31">
            <Flexer grow padding="0 2 0 0">
                <Select items={HandwritingFontList} selected={selected} onChange={x => onChange(x.label)} menuHeight="25rem" renderItems={
                    (items, selectedItem, onSelect) => items.map(tm =>
                        <Select.MenuItem key={tm.name} onClick={onSelect(tm)}>
                            <Select.MenuItemText style={{ fontFamily: tm.label }}>
                                {tm.label}
                            </Select.MenuItemText>
                            {tm === selectedItem && <Select.MenuItemCheck />}
                        </Select.MenuItem>
                    )
                } />
                <Spacer />
                <Copy size=".75">
                    Controls the handwriting that your stickies are personalized with.
                </Copy>
            </Flexer>
            <Flexer shrink>
                <FakeSticky style={styling}>
                    Almost before we knew it, we had left the ground.
                </FakeSticky>
            </Flexer>
        </Flexer>
    </>;
});

const ColorList = styled.div`
    display: grid;
    grid-template-columns: repeat(10, 2.8rem);
    grid-template-rows: repeat(3, 2.4rem);
    column-gap: .375rem;
    row-gap: .375rem;
`;

const ColorOption = styled.div <{ color: string, active?: boolean }>`
    border-radius: .25rem;
    background: ${p => p.color};

    &:hover {
        cursor: pointer;
        box-shadow: 0 0 0 2px ${p => darken(.1, p.color)}, 0 0 0 2px ${p => darken(.1, p.color)} inset;
    }
    &:active {
        box-shadow: 0 0 0 2px ${p => darken(.2, p.color)}, 0 0 0 2px ${p => darken(.2, p.color)} inset;
    }
    
    ${p => p.active ? `box-shadow: 0 0 0 2px ${darken(.2, p.color)}, 0 0 0 2px ${darken(.2, p.color)} inset;` : ''}
`;

export const PersonalizationColors = [

    Colors.green400,
    Colors.teal400,
    Colors.blue400,
    Colors.indigo400,
    Colors.purple400,
    Colors.pink400,
    Colors.red400,
    Colors.orange400,
    Colors.yellow400,
    Colors.gray400,

    Colors.green600,
    Colors.teal600,
    Colors.blue600,
    Colors.indigo600,
    Colors.purple600,
    Colors.pink600,
    Colors.red600,
    Colors.orange600,
    Colors.yellow600,
    Colors.gray600,

    Colors.green800,
    Colors.teal800,
    Colors.blue800,
    Colors.indigo800,
    Colors.purple800,
    Colors.pink800,
    Colors.red800,
    Colors.orange800,
    Colors.yellow800,
    Colors.gray800,
];

const ColorView = React.memo<{ color: string, onChange: VoidCallback1<string> }>(({ color, onChange }) => <>
    <Copy>Your Color:</Copy>
    <ColorList>
        {PersonalizationColors.map(x =>
            <ColorOption active={x === color} key={x} color={x} onClick={() => onChange(x)} />
        )}
    </ColorList>
    <Spacer />
    <Copy flush size=".75">
        Controls your user icon and activity indicator colours, as well as the colour of<br /> your vanishing pen.
    </Copy>
    <Spacer />
    <Flexer flex="row" align="center">
        <Counter size={2.5} color={color}>
            {useResolve(ProfileModel).initials}
        </Counter>
        <Flexer margin="0 1">
            <svg height="40" viewBox="0 0 206 56" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    stroke={color} strokeWidth="5" strokeLinecap="round"
                    d="M91.899 3C90.8123 10.4415 87.9773 17.7052 84.4448 24.4589C78.4079 36.0007 68.4788 44.1557 55.3402 48.532C41.9311 52.9985 27.0427 53.9329 12.9651 52.1485C9.93093 51.7639 -1.59636 50.565 5.01237 46.2663C8.34003 44.1018 12.3335 42.8775 16.2174 41.8874C24.7751 39.7059 33.6442 38.6563 42.4971 38.2274C63.7519 37.1976 85.2403 38.2302 106.475 39.1642C138.768 40.5846 170.722 42.2311 203 39.8613" />
            </svg>
        </Flexer>
    </Flexer>
</>);

export const PersonalizationView = () => {

    const api = useResolve(ApiClient);
    const me = useResolve(ProfileModel);
    const [state, setState] = useState(Obj.pick(me.getState(), 'color', 'handwriting'));

    const [onSubmit, _working, result] = useSubmitter(async (patch: Partial<typeof state>) => {
        const before = Object.fromEntries(Object.keys(patch).map(x => [x, state[x]]));
        setState(x => ({ ...x, ...patch }));
        try {
            await api.exec(v2.me.update(patch));
        }
        catch (e) {
            setState(x => ({ ...x, ...before }));
            throw e;
        }
    }, [state]);

    return <Flexer width="31">
        <HandwritingView handwriting={state.handwriting} onChange={x => onSubmit({ handwriting: x })} />
        <Spacer f={3} />
        <ColorView color={state.color ?? Colors.blue500} onChange={x => onSubmit({ color: x })} />
        <Spacer f={3} />
        <Flexer margin="2 0">
            <ResultInfoForSubmitter result={result} onSuccess="Changes saved." />
        </Flexer>
    </Flexer>;
};
